"use client";

import { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartData,
  ChartOptions,
} from "chart.js";
import { Pie, Doughnut, Line, Bar } from "react-chartjs-2";
import { CHART_THEME } from "@/lib/constants/chartPalette";

// Register Chart.js components including Filler for area charts
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface CustomChartProps {
  type: "pie" | "doughnut" | "line" | "bar";
  data: ChartData<"pie" | "doughnut" | "line" | "bar">;
  title?: string;
  height?: number;
}

export default function Chart({ type, data, title, height = 300 }: CustomChartProps) {
  const [isMounted, setIsMounted] = useState(false);
  const [theme, setTheme] = useState<"light" | "dark">("dark");
  const [responsiveHeight, setResponsiveHeight] = useState(height);

  useEffect(() => {
    setIsMounted(true);
    if (typeof window !== "undefined") {
      const htmlEl = document.documentElement;
      const initialTheme = (htmlEl.getAttribute("data-theme") as "light" | "dark") || "dark";
      setTheme(initialTheme);

      // Responsive height: shrink chart on small screens
      const updateHeight = () => {
        const w = window.innerWidth;
        if (w < 480) {
          setResponsiveHeight(Math.min(height, 220));
        } else if (w < 768) {
          setResponsiveHeight(Math.min(height, 260));
        } else {
          setResponsiveHeight(height);
        }
      };
      updateHeight();
      window.addEventListener("resize", updateHeight);

      const observer = new MutationObserver(() => {
        const newTheme = (htmlEl.getAttribute("data-theme") as "light" | "dark") || "dark";
        setTheme(newTheme);
      });

      observer.observe(htmlEl, { attributes: true, attributeFilter: ["data-theme"] });
      return () => {
        observer.disconnect();
        window.removeEventListener("resize", updateHeight);
      };
    }
  }, [height]);

  if (!isMounted) {
    return <div style={{ height: responsiveHeight }} className="chart-loading-placeholder" />;
  }

  const isLight = theme === "light";
  const chartTheme = isLight ? CHART_THEME.light : CHART_THEME.dark;
  const textColor = chartTheme.text;
  const gridColor = chartTheme.grid;
  const tooltipBg = chartTheme.tooltipBg;
  const tooltipTitle = chartTheme.tooltipTitle;
  const tooltipBorder = chartTheme.tooltipBorder;

  // Check if data is completely zero/empty for pie/doughnut charts
  const isAllZero = () => {
    if (type !== "pie" && type !== "doughnut") return false;
    const dataset = data.datasets[0];
    if (!dataset || !dataset.data) return true;
    return dataset.data.every((val) => val === 0);
  };

  // Standard premium options for dark theme
  const getOptions = (): ChartOptions<any> => {
    const isPieOrDoughnut = type === "pie" || type === "doughnut";
    // Bar/line legends print each dataset's `label`; several calculator
    // fallbacks ship label-less single datasets (designed as slice charts),
    // which rendered a literal "undefined" legend chip. No labels -> no
    // legend: the axis ticks already name every bar.
    const hasDatasetLabels = data.datasets.some((d) => typeof (d as { label?: string }).label === "string" && (d as { label?: string }).label !== "");
    const showLegend = isPieOrDoughnut || hasDatasetLabels;

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: showLegend,
          position: "bottom" as const,
          labels: {
            color: textColor,
            font: {
              family: "Inter, sans-serif",
              size: 11,
              weight: "500",
            },
            padding: 16,
            usePointStyle: true,
            pointStyle: "circle",
          },
        },
        tooltip: {
          backgroundColor: tooltipBg,
          titleColor: tooltipTitle,
          bodyColor: textColor,
          borderColor: tooltipBorder,
          borderWidth: 1,
          padding: 12,
          cornerRadius: 8,
          bodyFont: {
            family: "Inter, sans-serif",
          },
          titleFont: {
            family: "Outfit, sans-serif",
            weight: "bold",
          },
        },
      },
      ...(!isPieOrDoughnut && {
        scales: {
          x: {
            grid: {
              color: gridColor,
            },
            ticks: {
              color: CHART_THEME.dark.muted, // var(--text-muted)
              font: {
                family: "Inter, sans-serif",
                size: 10,
              },
            },
          },
          y: {
            grid: {
              color: gridColor,
            },
            ticks: {
              color: CHART_THEME.dark.muted,
              font: {
                family: "Inter, sans-serif",
                size: 10,
              },
            },
          },
        },
      }),
    };
  };

  const options = getOptions();

  // If pie chart data has only zeros, render a clean fallback visual instead of a blank circle or crash
  if (isAllZero()) {
    const fallbackData: ChartData<"doughnut"> = {
      labels: ["No active data"],
      datasets: [
        {
          data: [1],
          backgroundColor: [chartTheme.fallbackFill],
          borderWidth: 1,
          borderColor: chartTheme.fallbackBorder,
        },
      ],
    };
    
    return (
      <div className="chart-container" style={{ height }}>
        {title && <h4 className="chart-title">{title}</h4>}
        <Doughnut data={fallbackData} options={{
          ...options,
          plugins: {
            ...options.plugins,
            legend: { display: false },
            tooltip: { enabled: false },
          }
        }} />
        <div className="chart-fallback-label">Enter inputs to see breakdown</div>
        <style jsx>{`
          .chart-container {
            position: relative;
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          .chart-title {
            font-family: var(--font-heading);
            font-size: var(--text-base);
            color: var(--text-secondary);
            font-weight: var(--fw-extrabold);
            margin-bottom: var(--space-4);
            text-align: center;
          }
          .chart-fallback-label {
            position: absolute;
            top: 55%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-size: var(--text-xs);
            color: var(--text-muted);
            font-weight: var(--fw-bold);
            text-align: center;
            max-width: 140px;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="chart-container" style={{ height: responsiveHeight }}>
      {title && <h4 className="chart-title">{title}</h4>}
      <div style={{ flex: 1, position: "relative", width: "100%", height: "100%" }}>
        {type === "pie" && <Pie data={data as any} options={options} />}
        {type === "doughnut" && <Doughnut data={data as any} options={options} />}
        {type === "line" && <Line data={data as any} options={options} />}
        {type === "bar" && <Bar data={data as any} options={options} />}
      </div>

      <style jsx>{`
        .chart-container {
          width: 100%;
          display: flex;
          flex-direction: column;
        }
        .chart-title {
          font-family: var(--font-heading);
          font-size: var(--text-base);
          color: var(--text-secondary);
          margin-bottom: var(--space-4);
          text-align: center;
        }
        .chart-loading-placeholder {
          width: 100%;
          background-color: var(--overlay-a01);
          border-radius: var(--radius-md);
          border: 1px solid var(--border-color);
          animation: pulse 1.5s infinite ease-in-out;
        }
        @keyframes pulse {
          0% { opacity: 0.6; }
          50% { opacity: 0.3; }
          100% { opacity: 0.6; }
        }
      `}</style>
    </div>
  );
}
